'use client';

import { Button, Link, Typography } from '@curology/ui-components-web/react';
import CircleCheckIcon from '@curology/ui-components-web/react/Icon/icons/CircleCheckIcon';
import { type AxiosError } from 'axios';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { post, type ValidationErrorData } from '@/utils/http/client';

import { BodyCleanseModal } from './components/bodyCleanseModal';
import { ProductCard } from './productCard';
import { useNavigation } from '../../hooks/useNavigation';

const features = [
  'Made with Rx ingredients picked for you by your dermatology provider',
  'Designed to be more effective at clearing and preventing body acne than off-the-shelf body washes',
  'Helpful for active lifestyles and during hot summer months',
];

export const ClientContainer = () => {
  const { onNext } = useNavigation();
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const flash = useFlashMessages();

  useEffect(() => {
    trackEvent(AnalyticsEvent.UPSELL_VIEWED, {
      upsell: 'BODY_CLEANSE',
    });
  }, []);

  const acceptUpsell = () => {
    setProcessing(true);
    post<{ notification: { title: string; message: string } }>(
      '/api/upsells/body-cleanse-large/subscribe',
    )
      .then((response) => {
        flash.success({
          title: response.data.notification.title || 'Success!',
          message: response.data.notification.message,
        });
        trackEvent(AnalyticsEvent.UPSELL_ACCEPTED, {
          upsell: 'BODY_CLEANSE',
        });
        onNext();
      })
      .catch((error: AxiosError<ValidationErrorData>) => {
        flash.error({
          title: 'Sorry!',
          message:
            error.response?.data.message ||
            'An error occurred while processing your request.',
        });
        trackEvent(AnalyticsEvent.UPSELL_FAILED, {
          upsell: 'BODY_CLEANSE',
          error: error.response?.data.message || error.message,
        });
      })
      .finally(() => setProcessing(false));
  };

  return (
    <>
      <section className="cur-mb-25">
        <Typography size="h2">
          Boost your routine with Body Cleanse<sup>Rx</sup> for{' '}
          <span className="cur-text-brand">all-over</span> acne
        </Typography>
        <div className="cur-mt-4">
          <ProductCard />
        </div>
        <Typography className="cur-mt-6" size="h2">
          Add Rx power to your shower with a{' '}
          <span className="cur-text-brand">custom Rx</span> body wash
        </Typography>
        <ul className="cur-mt-4 cur-space-y-4 cur-bg-white cur-p-4">
          {features.map((feature, index) => (
            <li key={index} className="cur-flex cur-items-start cur-space-x-4">
              <CircleCheckIcon className="cur-w-6 cur-h-6 cur-flex-shrink-0 cur-text-surface-brand-accent" />
              <Typography size="body">{feature}</Typography>
            </li>
          ))}
        </ul>
        <Typography className="cur-mt-6" size="meta">
          After your 30-day trial ends, your recurring subscription box will be
          $19.55 more with each billing cycle with Body Cleanse
          <sup>Rx</sup> (60-day supply).{' '}
          <Link
            href="#"
            onClick={(event) => {
              event.preventDefault();
              setOpen(true);
            }}
          >
            See product ingredients & more
          </Link>
        </Typography>
        <Typography className="cur-mt-4" size="meta">
          Rx products are subject to consultation. If your dermatology provider
          cannot prescribe one due to any reason, it will be removed, and the
          cost of your subscription may be lower.
        </Typography>
      </section>

      <div
        className={clsx(
          'cur-fixed cur-bottom-0 cur-left-0 cur-w-full',
          !open && 'cur-z-[999999]',
        )}
      >
        <div className="cur-bg-white cur-flex cur-justify-center cur-items-center cur-space-x-2 cur-p-4">
          <Button
            onClick={() => {
              trackEvent(AnalyticsEvent.UPSELL_DECLINED, {
                upsell: 'BODY_CLEANSE',
              });
              onNext();
            }}
            className="cur-w-[155px]"
            buttonType="secondary"
          >
            Skip
          </Button>
          <Button
            loading={processing}
            onClick={acceptUpsell}
            className="cur-w-[155px]"
          >
            Add for $14
          </Button>
        </div>
      </div>

      <BodyCleanseModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};
